import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, User, Phone, Mail, Calendar,
  AlertTriangle, CheckCircle, Edit, Trash2, Archive,
  Camera, FileText, Loader2
} from 'lucide-react';
import { compressImage, getThumbnail, cleanupOldImages } from '../../../../utils/imageCompression';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
import { useVessel } from '../../../../contexts/VesselContext';
// import { useVesselCollections } from '../../../../hooks/useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { COLLECTIONS, createBaseRecord } from '../../../../types/core';
import { getDrillRequirements } from '../../../../config/drillRequirements';
import CrewDrillHistoryModal from '../../CrewDrillHistoryModal';
import DrillParticipationCard from './DrillParticipationCard';
import TrainingHistoryCard from './TrainingHistoryCard';
import HoursOfRestSummaryCard from './HoursOfRestSummaryCard';
import CertificatesSummaryCard from './CertificatesSummaryCard';
import { useMergedCertificates } from '../../../../hooks/domain/crew/useCrewCertificates';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useTrainingData } from '../../../../hooks/domain/useTrainingData';
import { useStorageState } from '../../../../hooks/useStorageState';
import { filterTombstoned } from '../../../../sync/tombstone';  // FT-458: hide soft-deleted drills from crew participation
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';
import { getExpiryStatus } from '../../crew/profileHelpers';

const CrewProfileView = ({ crew, onBack, onEdit, onDelete, onArchive }) => {
  const { can } = usePermissions();
  const { showConfirm, ConfirmDialog } = useCustomDialogs();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // Collections setup - GPT Pattern B
  const { currentVesselId, getCurrentVessel } = useVessel();
  // const { vesselId, collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration
  const vesselId = currentVesselId; // Use currentVesselId from VesselContext
  const currentVessel = getCurrentVessel();

  // Load drills using Anchor Mode v2.0 pattern (automatic vessel scoping)
  // FT-458: filter soft-deleted (tombstoned) drills — a deleted drill must not
  // count toward a crew member's drill participation record.
  const { value: rawDrillsData = [] } = useStorageState('drills', []);
  const drillsData = filterTombstoned(rawDrillsData);

  // Profile photos storage (org-scoped)
  const { value: profilePhotos = {}, setValue: setProfilePhotos } = useStorageState('crew_photos', {});

  // Training data hook for certificate merging and training history
  const { trainingRecords } = useTrainingData();

  // Add swipe-to-go-back functionality
  const { containerRef, swipeProgress } = useSwipeBack(onBack);

  // Track previous vessel ID for vessel switching detection
  const prevVesselIdRef = useRef(vesselId);

  // Force update counter for reactive data changes
  const [updateCounter, setUpdateCounter] = useState(0);
  const forceUpdate = useCallback(() => setUpdateCounter(c => c + 1), []);

  // Handle delete with confirmation
  const handleDeleteClick = useCallback(async () => {
    const confirmed = await showConfirm(
      'Delete Crew Member?',
      `Are you sure you want to permanently delete ${crew.full_name}? This action cannot be undone and will remove all associated records.`,
      'Delete',
      'Cancel',
      'danger'
    );

    if (confirmed) {
      setIsDeleting(true);
      try {
        await onDelete(crew.id);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [crew.id, crew.full_name, onDelete, showConfirm]);

  // Handle archive with confirmation
  const handleArchiveClick = useCallback(async () => {
    const confirmed = await showConfirm(
      'Archive Crew Member?',
      `Archive ${crew.full_name}? Archived crew members will be hidden from the main list but can be restored later.`,
      'Archive',
      'Cancel',
      'warning'
    );

    if (confirmed) {
      setIsArchiving(true);
      try {
        await onArchive(crew.id);
      } finally {
        setIsArchiving(false);
      }
    }
  }, [crew.id, crew.full_name, onArchive, showConfirm]);

  // State management (drills now loaded via useStorageState hook above)
  const [state, setState] = useState({
    profilePhoto: null,
    certificates: [],
    loading: true,
    error: null
  });
  const [showDrillHistory, setShowDrillHistory] = useState(false);

  // GPT Pattern B - Core functions
  const loadCrewData = async () => {
    if (!vesselId || !crew?.id) return;

    try {
      setState(s => ({ ...s, loading: true, error: null }));

      // Load certificates from crew object (embedded by useCrewCertificates)
      const crewCerts = crew.certificates || [];

      // Drills now loaded via useStorageState hook (drillsData)

      // Load profile photo from org-scoped storage
      const photoData = profilePhotos[crew.id] || null;

      setState({
        certificates: crewCerts,
        profilePhoto: photoData?.imageData || null,
        loading: false,
        error: null
      });
    } catch (error) {

      setState(s => ({ ...s, loading: false, error: error.message || 'Load failed' }));
    }
  };

  const saveProfilePhoto = async (imageData, thumbnail) => {
    if (!vesselId || !crew?.id) return;

    try {
      // Save to org-scoped storage
      const photoData = {
        id: crew.id,
        vesselId: vesselId,
        crewId: crew.id,
        imageData,
        thumbnail,
        uploadedAt: new Date().toISOString(),
        origin: 'manual'
      };

      // Update photos object with new photo
      setProfilePhotos(prev => ({
        ...prev,
        [crew.id]: photoData
      }));

      setState(s => ({ ...s, profilePhoto: imageData }));
    } catch (error) {

      throw error;
    }
  };

  // React effect - load on mount / vessel change (GPT Pattern B)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!vesselId || !crew?.id) return;
      try {
        await loadCrewData();
      } catch (error) {
        if (alive) console.error('[CrewProfileView] Effect error:', error);
      }
    })();
    return () => { alive = false; };
  }, [vesselId, crew?.id, profilePhotos]);

  // Phase 5: Storage event listener for cross-module updates
  useEffect(() => {
    const handleStorageUpdate = (e) => {
      const { module, vesselId: eventVesselId, key } = e.detail || {};

      // Only process events for current vessel
      if (eventVesselId && eventVesselId !== vesselId) return;

      // Handle different module updates
      switch (module) {
        case 'crew':
          // Crew data updated (certificates, profile changes)
          // The crew prop should update via parent, but force refresh drills too
          loadCrewData();
          break;

        case 'training':
        case 'onboard_training':
          // Training sessions added/updated - affects certificates & training history
          // Training hook auto-refreshes, force component re-render
          forceUpdate();
          break;

        case 'operations':
        case 'hours_of_rest':
          // Hours of rest data updated
          forceUpdate();
          break;

        case 'safety':
        case 'drills':
          // Drills added/updated - reload drill data
          loadCrewData();
          break;

        default:
          // Check key pattern for module detection
          if (key?.startsWith('drills') || key?.startsWith('safety')) {
            loadCrewData();
          } else if (key?.startsWith('training') || key?.startsWith('onboard_training')) {
            forceUpdate();
          } else if (key?.startsWith('hours_of_rest') || key?.startsWith('operations')) {
            forceUpdate();
          }
          break;
      }
    };

    // Listen for storage-sync events (sr:storage:set)
    window.addEventListener('sr:storage:set', handleStorageUpdate);

    return () => {
      window.removeEventListener('sr:storage:set', handleStorageUpdate);
    };
  }, [vesselId, forceUpdate]);

  // Phase 5: Vessel switching detection
  useEffect(() => {
    if (prevVesselIdRef.current && prevVesselIdRef.current !== vesselId) {

      // Reload all data for new vessel
      loadCrewData();
      forceUpdate();
    }
    prevVesselIdRef.current = vesselId;
  }, [vesselId, forceUpdate]);

  // Get merged certificates from domain hook (Phase 5 enhancement)
  const mergedCertificates = useMergedCertificates(crew?.id);

  // Show loading state
  if (state.loading && !state.profilePhoto && drillsData.length === 0) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading crew profile...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Profile</h3>
          <p className="text-gray-500 mb-4">{state.error}</p>
          <button
            onClick={() => {
              setState(s => ({ ...s, error: null, loading: true }));
              loadCrewData();
            }}
            className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!crew) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get drill participation for this crew member (using collections data)
  const getDrillParticipation = () => {
    const vesselRequirements = currentVessel ? getDrillRequirements(currentVessel) : {};
    const requiredDrills = Object.values(vesselRequirements).filter(req => req.compliance_level === 'Required');
    
    const participation = {};
    
    requiredDrills.forEach(requirement => {
      const drillType = requirement.drill_type;
      
      // Find all drills of this type that the crew member participated in
      const participatedDrills = drillsData.filter(drill => 
        drill.type === drillType && 
        drill.participants?.some(p => {
          // Handle both object format {id, name, position} and string format
          if (typeof p === 'object' && p !== null) {
            return p.id === crew.id;
          }
          return p === crew.id;
        })
      );
      
      // Get the most recent drill
      const lastDrill = participatedDrills
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      
      const daysSinceLastDrill = lastDrill 
        ? Math.floor((new Date() - new Date(lastDrill.date)) / (1000 * 60 * 60 * 24))
        : null;
      
      // Check if overdue based on frequency
      const isOverdue = daysSinceLastDrill === null || 
        daysSinceLastDrill > requirement.frequency_days;
      
      participation[drillType] = {
        drillName: requirement.drill_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        lastParticipation: lastDrill ? lastDrill.date : null,
        daysSince: daysSinceLastDrill,
        frequency: requirement.frequency_days,
        isOverdue,
        participationCount: participatedDrills.length
      };
    });
    
    return participation;
  };

  const drillParticipation = getDrillParticipation();

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressedImage = await compressImage(file, 100); // 100KB for profile photos
        const thumbnail = await getThumbnail(compressedImage, 200);
        
        // Save to collections (vessel-scoped)
        await saveProfilePhoto(compressedImage, thumbnail);
        
        // Cleanup old localStorage images (migration cleanup)
        cleanupOldImages(crew.id);
      } catch (error) {

        alert('Error processing image. Please try a smaller image.');
      }
    }
  };

  // FT-28: medicalStatus removed - ENG1/ML5 now tracked via certificates array

  return (
    <>
    <div className="min-h-screen bg-slate-100" ref={containerRef}>
      {/* Swipe Indicator */}
      <SwipeIndicator progress={swipeProgress} />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                Crew Profile
              </h1>
            </div>
            
            {/* Action buttons - only show for Fleet/Skipper */}
            <div className="flex items-center gap-2">
              {can('update', 'crew') && (
                <>
                  <button
                    onClick={() => onEdit(crew)}
                    className="inline-flex items-center gap-2 px-2 sm:px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                  <button
                    onClick={handleArchiveClick}
                    disabled={isArchiving}
                    className="inline-flex items-center gap-2 px-2 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isArchiving ? 'Archiving...' : 'Archive'}
                  >
                    {isArchiving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Archive className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">{isArchiving ? 'Archiving...' : 'Archive'}</span>
                  </button>
                </>
              )}
              {can('delete', 'crew') && (
                <button
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  className="inline-flex items-center gap-2 px-2 sm:px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isDeleting ? 'Deleting...' : 'Delete'}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">{isDeleting ? 'Deleting...' : 'Delete'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 pb-24">
        {/* Compliance Status Summary Card */}
        {(() => {
          // Calculate compliance summary using merged certificates (crew + training)
          const expiredCerts = mergedCertificates.filter(c => {
            const days = getDaysUntilExpiry(c.expiry_date || c.expiry);
            return days !== null && days < 0;
          });
          const expiringCerts = mergedCertificates.filter(c => {
            const days = getDaysUntilExpiry(c.expiry_date || c.expiry);
            return days !== null && days >= 0 && days < 30;
          });
          const overdueDrills = Object.values(drillParticipation).filter(d => d.isOverdue);

          const hasIssues = expiredCerts.length > 0 || expiringCerts.length > 0 || overdueDrills.length > 0;
          const hasCritical = expiredCerts.length > 0;

          if (!hasIssues) {
            return (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-800">All certificates valid and drills up to date</span>
                </div>
              </div>
            );
          }

          return (
            <div className={`${hasCritical ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4 mb-4`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`h-5 w-5 mt-0.5 ${hasCritical ? 'text-red-600' : 'text-amber-600'}`} />
                <div className="flex-1">
                  <h4 className={`text-sm font-semibold ${hasCritical ? 'text-red-800' : 'text-amber-800'}`}>
                    Attention Required
                  </h4>
                  <ul className="mt-1 space-y-1 text-sm">
                    {expiredCerts.length > 0 && (
                      <li className="text-red-700">
                        <span className="font-medium">{expiredCerts.length} expired certificate{expiredCerts.length !== 1 ? 's' : ''}</span>
                        {expiredCerts.length <= 2 && `: ${expiredCerts.map(c => c.type || c.name || 'Certificate').join(', ')}`}
                      </li>
                    )}
                    {expiringCerts.length > 0 && (
                      <li className="text-amber-700">
                        <span className="font-medium">{expiringCerts.length} certificate{expiringCerts.length !== 1 ? 's' : ''} expiring within 30 days</span>
                        {expiringCerts.length <= 2 && `: ${expiringCerts.map(c => c.type || c.name || 'Certificate').join(', ')}`}
                      </li>
                    )}
                    {overdueDrills.length > 0 && (
                      <li className="text-amber-700">
                        <span className="font-medium">{overdueDrills.length} overdue drill{overdueDrills.length !== 1 ? 's' : ''}</span>
                        {overdueDrills.length <= 3 && `: ${overdueDrills.map(d => d.drillName).join(', ')}`}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-2 space-y-4">
            {/* Basic Information Card */}
            <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
              <div className="bg-slate-700 px-5 py-3">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-white" />
                  <h3 className="text-base font-semibold text-white">Basic Information</h3>
                </div>
              </div>
              <div className="px-6 py-5">
                <div className="flex items-start gap-6">
                  {/* Profile Photo */}
                  <div className="flex-shrink-0">
                    <div className="relative group">
                      {state.profilePhoto ? (
                        <img
                          src={state.profilePhoto}
                          alt={crew.full_name}
                          className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg bg-gray-100 flex items-center justify-center">
                          <User className="h-12 w-12 text-gray-400" />
                        </div>
                      )}
                      {/* Desktop: Hover overlay for photo upload */}
                      <label className="absolute inset-0 hidden md:flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-lg">
                        <Camera className="h-6 w-6 text-white" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {/* Mobile: Photo upload button (touch-friendly) */}
                    <label className="md:hidden mt-2 inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-md cursor-pointer transition-colors">
                      <Camera className="h-4 w-4" />
                      Change Photo
                      <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                    </label>
                  </div>

                  {/* Basic Info */}
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900">{crew.full_name}</h2>
                    <p className="text-lg text-gray-600 mt-1">{crew.position}</p>
                    
                    <div className="mt-4 space-y-2">
                      {crew.identification && (
                        <div className="flex items-center text-sm text-gray-600">
                          <FileText className="h-4 w-4 mr-2 text-gray-400" />
                          ID: {crew.identification}
                        </div>
                      )}
                      {crew.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="h-4 w-4 mr-2 text-gray-400" />
                          {crew.phone}
                        </div>
                      )}
                      {crew.email && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="h-4 w-4 mr-2 text-gray-400" />
                          {crew.email}
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        Start Date: {formatDate(crew.start_date)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FT-28: Medical Information card removed - ENG1/ML5 now shown in CertificatesSummaryCard */}

            {/* Emergency Contact */}
            <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
              <div className="bg-slate-700 px-5 py-3">
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-white" />
                  <h3 className="text-base font-semibold text-white">Emergency Contact</h3>
                </div>
              </div>
              <div className="px-6 py-5">
                {crew.emergency_contact || crew.emergency_phone ? (
                  <div className="space-y-3">
                    {crew.emergency_contact && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Contact Name</span>
                        <p className="text-gray-900">{crew.emergency_contact}</p>
                      </div>
                    )}
                    {crew.emergency_phone && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Contact Phone</span>
                        <p className="text-gray-900">{crew.emergency_phone}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No emergency contact recorded</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Certificates & Notes */}
          <div className="space-y-4">
            {/* Certificates Summary Card - Phase 5 enhancement */}
            <CertificatesSummaryCard crewId={crew.id} />

            {/* Drill Participation */}
            <DrillParticipationCard
              drillParticipation={drillParticipation}
              drills={drillsData}
              crewId={crew.id}
              onViewHistory={() => setShowDrillHistory(true)}
            />

            {/* Training History */}
            <TrainingHistoryCard
              trainingRecords={trainingRecords}
              crewId={crew.id}
            />

            {/* Hours of Rest Summary */}
            <HoursOfRestSummaryCard
              crewId={crew.id}
            />

            {/* Notes */}
            {crew.notes && (
              <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
                <div className="bg-slate-700 px-5 py-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-white" />
                    <h3 className="text-base font-semibold text-white">Notes</h3>
                  </div>
                </div>
                <div className="px-6 py-5">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{crew.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Drill History Modal */}
    <CrewDrillHistoryModal
      isOpen={showDrillHistory}
      onClose={() => setShowDrillHistory(false)}
      crewMember={crew}
      drillParticipation={drillParticipation}
      drills={drillsData}
    />

    {/* Confirmation Dialog */}
    <ConfirmDialog />
  </>
  );
};

export default CrewProfileView;